import { LearnPagesData, MobileLearnPage } from '@osmosis/shared/types/learn';
import { AxiosGetType } from '../../../context/FetchProvider';
import { AxiosPostType } from '../../../context/FetchProvider';
import { VIDEO_RECOMMENDATIONS } from '../../shared/learn/recommendations';
import { GetLearnPageResponse } from '../../learn';
import { LanguageCode } from '@elsevier-health/osmosis-shared/languages';
import { ParsedQuery } from '../../../context/PagesProvider';

/**
 * Gets learn data via new blocks fetch.
 */
export const getLearnPage = async (
  get: AxiosGetType,
  {
    learnNodeID,
    query,
    from,
    language,
    nocache,
    productID,
  }: {
    learnNodeID?: number;
    query?: string;
    from?: string;
    language?: LanguageCode;
    nocache?: string;
    productID?: number;
  },
): Promise<GetLearnPageResponse> => {
  const { data } = await get<GetLearnPageResponse>('/learnData', {
    params: {
      learnNodeID,
      query,
      from,
      language,
      nocache,
      productID,
    },
  });

  return data;
};

/**
 * Get learn pages data.
 */
export const getLearnPages = async (
  post: AxiosPostType,
  program: string,
): Promise<LearnPagesData[]> => {
  const concepts = VIDEO_RECOMMENDATIONS[program.toUpperCase()];

  const res = await post<
    { concepts: Record<string, LearnPagesData> },
    { concepts: string[]; videosOnly: boolean }
  >('/getLearnPages', {
    concepts,
    videosOnly: true,
  });

  if (res.data.concepts) {
    return Object.values(res.data.concepts);
  }

  return [];
};

/**
 * Gets the Learn pages for an ordered list of Learn node IDs.
 */
export const getLearnPagesByNodeIDs = async (
  post: AxiosPostType,
  learnNodeIDs: readonly number[],
): Promise<MobileLearnPage[]> => {
  try {
    const res = await post<
      { learnPages?: MobileLearnPage[] },
      { learnNodeIDs: number[]; videosOnly: boolean }
    >('/getLearnPages', {
      learnNodeIDs: [...learnNodeIDs],
      videosOnly: true,
    });

    return res.data.learnPages ?? [];
  } catch {
    return [];
  }
};

/**
 * Get the URL for a learn page.
 * @param concept - The concept or concepts.
 * @param language - The language code.
 * @param query - The parsed query parameters.
 * @returns The URL for the learn page.
 */
export function getLearnUrl({
  concept,
  language,
  query,
}: {
  concept: string | string[];
  language: LanguageCode | null;
  query: ParsedQuery | Record<string, string>;
}): string {
  const searchParams = new URLSearchParams(query as Record<string, string>);

  const conceptPart = [concept].flat().join('/');
  const languagePart =
    language && language !== LanguageCode.EN ? `${language}/` : '';
  const searchPart = searchParams.toString() ? `?${searchParams}` : '';

  return `/learn/${languagePart}${conceptPart}` + searchPart;
}
